'use client';

import { c as _c } from "react/compiler-runtime";
import { hashDiagnosticEntity, useGetMarketBackers } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { UserMultipleIcon } from '@/components/ui/icon/glyphs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { logger } from '@/lib/telemetry/logger';
export interface BackerItem {
  address: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  avatarPreferences?: {
    rasterise: boolean;
  } | null;
  outcomeIndex: number;
  outcomeLabel: string;
  amount: string;
  timestamp: string;
}
export interface ProposalBackersPanelProps {
  backers: BackerItem[];
  className?: string;
}
function combineBackersByAddress(backers: BackerItem[]): BackerItem[] {
  const combined = new Map<string, BackerItem>();
  for (const backer of backers) {
    const existing = combined.get(backer.address);
    if (existing === undefined) {
      combined.set(backer.address, backer);
      continue;
    }
    const latest = new Date(backer.timestamp).getTime() >= new Date(existing.timestamp).getTime() ? backer : existing;
    combined.set(backer.address, {
      ...latest,
      amount: (BigInt(existing.amount) + BigInt(backer.amount)).toString()
    });
  }
  return [...combined.values()];
}
const CURRENT_BACKERS_FIELDS = ['participationId', 'backer', 'shareBalanceD6', 'lastBackedAt'] as const;
export function LiveProposalBackersPanel(t0) {
  const $ = _c(25);
  const {
    proposalPubkey,
    className
  } = t0;
  let t1;
  if ($[0] !== proposalPubkey) {
    t1 = {
      pubkey: proposalPubkey,
      sortBy: "shares",
      sortOrder: "desc",
      limit: 20,
      fields: CURRENT_BACKERS_FIELDS
    };
    $[0] = proposalPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const query = useGetMarketBackers(t1);
  const rows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey);
  let t2;
  if ($[2] !== proposalPubkey) {
    t2 = hashDiagnosticEntity(proposalPubkey);
    $[2] = proposalPubkey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const proposalHash = t2;
  let t3;
  let t4;
  if ($[4] !== proposalHash || $[5] !== query.error || $[6] !== query.isError) {
    t3 = () => {
      if (!query.isError) {
        return;
      }
      logger.warn("proposal_detail.backers_fetch_failed", {
        proposalHash,
        errorClass: query.error instanceof Error ? query.error.name : "unknown",
        retryable: true
      });
    };
    t4 = [proposalHash, query.error, query.isError];
    $[4] = proposalHash;
    $[5] = query.error;
    $[6] = query.isError;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  if (query.isPending && rows.length === 0) {
    let t5;
    if ($[9] !== className) {
      t5 = <ProposalBackersSkeleton {...className !== undefined ? {
        className
      } : {}} />;
      $[9] = className;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (query.isError && rows.length === 0) {
    let t5;
    if ($[11] !== query) {
      t5 = () => void query.refetch();
      $[11] = query;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== className) {
      t6 = className !== undefined ? {
        className
      } : {};
      $[13] = className;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t5 || $[16] !== t6) {
      t7 = <ProposalBackersError onRetry={t5} {...t6} />;
      $[15] = t5;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  let t5;
  if ($[18] !== rows) {
    t5 = rows.map(_temp);
    $[18] = rows;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const backers = t5;
  let t6;
  if ($[20] !== className) {
    t6 = className !== undefined ? {
      className
    } : {};
    $[20] = className;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== backers || $[23] !== t6) {
    t7 = <ProposalBackersPanel backers={backers} {...t6} />;
    $[22] = backers;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp(row) {
  return {
    address: row.backer.walletAddress,
    displayName: row.backer.displayName,
    handle: row.backer.handle,
    avatarUrl: row.backer.avatarUrl,
    avatarNsfw: row.backer.avatarNsfw ?? false,
    avatarPreferences: row.backer.avatarPreferences ?? null,
    outcomeIndex: 0,
    outcomeLabel: "",
    amount: row.shareBalanceD6,
    timestamp: row.lastBackedAt
  };
}
function sortByAmountDesc(backers: BackerItem[]): BackerItem[] {
  return backers.toSorted((a, b) => {
    const ab = BigInt(a.amount);
    const bb = BigInt(b.amount);
    if (bb > ab) return 1;
    if (bb < ab) return -1;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}
export function ProposalBackersPanel(t0) {
  const $ = _c(19);
  const {
    backers,
    className
  } = t0;
  const tBackers = useTranslations("market.backers");
  const {
    formatD6
  } = useFormattedCurrency();
  let combinedBackers;
  let t1;
  if ($[0] !== backers) {
    combinedBackers = combineBackersByAddress(backers);
    t1 = sortByAmountDesc(combinedBackers);
    $[0] = backers;
    $[1] = combinedBackers;
    $[2] = t1;
  } else {
    combinedBackers = $[1];
    t1 = $[2];
  }
  const sorted = t1;
  const t2 = combinedBackers.length;
  let t3;
  if ($[3] !== className) {
    t3 = className !== undefined ? {
      className
    } : {};
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== formatD6) {
    t4 = b_0 => <BackerRow backer={b_0} amountLabel={formatD6(b_0.amount, "compact")} />;
    $[5] = formatD6;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== tBackers) {
    t5 = tBackers("empty_title");
    $[7] = tBackers;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <EmptyState size="compact" icon={UserMultipleIcon} title={t5} />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== sorted || $[12] !== t4 || $[13] !== t6) {
    t7 = <InfiniteList items={sorted} getItemKey={_temp2} renderItem={t4} hasNextPage={false} isFetchingNextPage={false} fetchNextPage={_temp3} emptyState={t6} maxHeight={320} />;
    $[11] = sorted;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== combinedBackers.length || $[16] !== t3 || $[17] !== t7) {
    t8 = <ProposalBackersShell testId="proposal-backers-panel" backerCount={t2} {...t3}>{t7}</ProposalBackersShell>;
    $[15] = combinedBackers.length;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp3() {}
function _temp2(b) {
  return b.address;
}
function ProposalBackersSkeleton(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = className !== undefined ? {
      className
    } : {};
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-3" aria-hidden="true"><BackerRowSkeleton /><BackerRowSkeleton /><BackerRowSkeleton /><BackerRowSkeleton /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <ProposalBackersShell testId="proposal-backers-skeleton" {...t1}>{t2}</ProposalBackersShell>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function BackerRowSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="size-8 shrink-0 rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex h-9 items-center justify-between gap-3"><div className="flex min-w-0 flex-1 items-center gap-3">{t0}<div className="flex min-w-0 flex-1 flex-col gap-1.5"><Skeleton className="h-3.5 w-28" /><Skeleton className="h-3 w-20" /></div></div><Skeleton className="h-3.5 w-16 shrink-0" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ProposalBackersError(t0) {
  const $ = _c(17);
  const {
    onRetry,
    className
  } = t0;
  const tBackers = useTranslations("market.backers");
  let t1;
  if ($[0] !== className) {
    t1 = className !== undefined ? {
      className
    } : {};
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tBackers) {
    t2 = tBackers("error_title");
    $[2] = tBackers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <h3 className="text-heading-sm text-foreground-primary">{t2}</h3>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== tBackers) {
    t4 = tBackers("error_retry");
    $[6] = tBackers;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onRetry || $[9] !== t4) {
    t5 = <AnimatedButton variant="secondary" onClick={onRetry}>{t4}</AnimatedButton>;
    $[8] = onRetry;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <div role="alert" className="flex flex-col items-center justify-center gap-4 py-8">{t3}{t5}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t6) {
    t7 = <ProposalBackersShell testId="proposal-backers-error" {...t1}>{t6}</ProposalBackersShell>;
    $[14] = t1;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function ProposalBackersShell(t0) {
  const $ = _c(16);
  const {
    testId,
    className,
    backerCount,
    children
  } = t0;
  const tDetail = useTranslations("launchpad.detail");
  let t1;
  if ($[0] !== backerCount) {
    t1 = backerCount !== undefined ? {
      "data-backer-count": String(backerCount)
    } : {};
    $[0] = backerCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tDetail) {
    t2 = tDetail("backersTitle");
    $[2] = tDetail;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <header className="text-body-lg font-medium text-foreground-primary">{t2}</header>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children) {
    t4 = <div className="min-h-48">{children}</div>;
    $[6] = children;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <PanelContent className="flex flex-col gap-4">{t3}{t4}</PanelContent>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== className || $[12] !== t1 || $[13] !== t5 || $[14] !== testId) {
    t6 = <Panel surface="solid" density="compact" className={className} data-testid={testId} {...t1}>{t5}</Panel>;
    $[11] = className;
    $[12] = t1;
    $[13] = t5;
    $[14] = testId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
interface BackerRowProps {
  backer: BackerItem;
  amountLabel: string;
}
function BackerRow(t0) {
  const $ = _c(31);
  const {
    backer,
    amountLabel
  } = t0;
  const intent = useUserProfilePrefetchIntent(backer.address);
  const t1 = backer.address;
  const t2 = `/portfolio/${backer.address}`;
  const t3 = backer.avatarPreferences?.rasterise ?? false;
  let t4;
  if ($[0] !== backer.address || $[1] !== backer.avatarNsfw || $[2] !== backer.avatarUrl || $[3] !== backer.displayName || $[4] !== t3) {
    t4 = <UserAvatar size="md" userId={backer.address} displayName={backer.displayName} avatarUrl={backer.avatarUrl} nsfw={backer.avatarNsfw} rasterise={t3} />;
    $[0] = backer.address;
    $[1] = backer.avatarNsfw;
    $[2] = backer.avatarUrl;
    $[3] = backer.displayName;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== backer.address || $[8] !== backer.avatarNsfw || $[9] !== backer.avatarUrl || $[10] !== backer.displayName || $[11] !== backer.handle) {
    t6 = {
      userId: null,
      walletAddress: backer.address,
      displayName: backer.displayName,
      handle: backer.handle,
      avatarUrl: backer.avatarUrl,
      avatarNsfw: backer.avatarNsfw,
      connectedProviders: t5
    };
    $[7] = backer.address;
    $[8] = backer.avatarNsfw;
    $[9] = backer.avatarUrl;
    $[10] = backer.displayName;
    $[11] = backer.handle;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== backer.address) {
    t7 = backer.address.slice(0, 4);
    $[13] = backer.address;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== backer.address) {
    t8 = backer.address.slice(-4);
    $[15] = backer.address;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = `${t7}…${t8}`;
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <UserIdentity user={t6} variant="stack" size="sm" fallback={t9} />;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== intent || $[21] !== t10 || $[22] !== t2 || $[23] !== t4) {
    t11 = <Link href={t2} className="flex min-w-0 items-center gap-3" {...intent}>{t4}{t10}</Link>;
    $[20] = intent;
    $[21] = t10;
    $[22] = t2;
    $[23] = t4;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== amountLabel) {
    t12 = <span className="shrink-0 text-body font-medium text-foreground-primary tabular-nums">{amountLabel}</span>;
    $[25] = amountLabel;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== backer.address || $[28] !== t11 || $[29] !== t12) {
    t13 = <div className="flex items-center justify-between gap-3" data-testid="proposal-backer-row" data-backer-address={t1}>{t11}{t12}</div>;
    $[27] = backer.address;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}